/**
 * Utilities for rendering background-agent tool calls (`read_agent`, `write_agent`,
 * `list_agents`). The tool result is a human-readable status string emitted by the
 * agent runtime (`formatAgentStatus`), shaped like:
 *
 *   `agent_id: abc123, agent_type: explore, status: running, description: ..., elapsed: 5s, ...`
 *
 * optionally followed by turn-history text. These helpers parse the leading status
 * fields so we can build a friendly title, mirroring the runtime's CLI timeline entry.
 */

import {capitalizeFirst} from '../../../utils/group-agent-logs'

export interface ParsedAgentStatus {
  agentType: string | null
  status: string | null
  description: string | null
}

const STATUS_LABELS: Record<string, string> = {
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
}

/**
 * Parses the runtime agent status log into structured fields. Returns `null` when no
 * expected fields are present, so callers can fall back to a generic title.
 */
export function parseAgentStatusLog(log: string | undefined): ParsedAgentStatus | null {
  if (!log) return null

  const agentType = log.match(/agent_type:\s*([^,\n]+)/)?.[1]?.trim() ?? null
  const description = log.match(/description:\s*([^,\n]+)/)?.[1]?.trim() ?? null
  const rawStatus = log.match(/status:\s*(\w+)/)?.[1]?.trim() ?? null

  if (!agentType && !description && !rawStatus) return null

  const status = rawStatus ? (STATUS_LABELS[rawStatus] ?? rawStatus) : null
  return {agentType, status, description}
}

export function buildReadAgentTitle(parsed: ParsedAgentStatus | null, agentId: string | undefined): string {
  return `Read agent: ${describeAgent(parsed, agentId)}`
}

export function buildWriteAgentTitle(parsed: ParsedAgentStatus | null, agentId: string | undefined): string {
  return `Message agent: ${describeAgent(parsed, agentId)}`
}

/** Builds the shared title fragment: `<Type> agent — <description>`, or the raw agent id. */
function describeAgent(parsed: ParsedAgentStatus | null, agentId: string | undefined): string {
  const agentType = parsed?.agentType
  if (agentType) {
    const label = `${capitalizeFirst(agentType)} agent`
    return parsed?.description ? `${label} — ${parsed.description}` : label
  }
  return agentId || 'unknown'
}
